/**
 * Goal Roadmap — horizontal scrollable timeline.
 * Numeric: Start → 12 month nodes → Finish (per-month targets)
 * Steps: Start → step nodes → Finish (completed/not)
 */

import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { Goal } from '@turnifly/shared/types'
import { parseMilestones, calculateProgress } from '@turnifly/shared/utils'
import { useGoalMonthlyProgress, useGoalEntries } from '@turnifly/shared/hooks'
import type { Milestone } from '@turnifly/shared/utils'

interface GoalRoadmapProps {
  goal: Goal
}

const MONTH_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'] as const

// ── Steps Roadmap ──

function StepsRoadmap({ goal }: GoalRoadmapProps): React.ReactElement {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const { data: entries } = useGoalEntries(goal.id)
  const steps = goal.steps ?? []
  const progress = calculateProgress(goal)

  const completedSet = useMemo(() => {
    const set = new Set<number>()
    for (const e of entries ?? []) {
      if (e.step_index !== null) set.add(e.step_index)
    }
    return set
  }, [entries])

  if (steps.length === 0) return <></>

  return (
    <div className="mt-2 mb-1">
      <div
        ref={scrollRef}
        className="flex items-center gap-0 overflow-x-auto lg:overflow-x-visible scrollbar-hide pb-1 -mx-1 px-1"
      >
        <div className="shrink-0 flex flex-col items-center mr-1 lg:mr-2">
          <span className="text-[10px] lg:text-xs">🏁</span>
          <span className="text-[8px] lg:text-[9px] text-text-hint mt-0.5">{t('goals.start')}</span>
        </div>

        {steps.map((step, idx) => {
          const isDone = completedSet.has(idx)
          return (
            <div key={idx} className="shrink-0 lg:shrink lg:flex-1 flex items-center">
              <div className={`w-4 lg:w-full lg:min-w-6 h-[2px] ${isDone ? 'bg-success/40' : 'bg-white/[0.06]'}`} />
              <div className="flex flex-col items-center">
                <div className={`w-5 h-5 lg:w-6 lg:h-6 rounded-full border flex items-center justify-center ${
                  isDone ? 'bg-success border-success/30' : 'bg-white/[0.08] border-white/[0.08]'
                }`}>
                  {isDone ? (
                    <svg viewBox="0 0 16 16" className="w-3 h-3 text-white">
                      <path d="M3 8l3 3 7-7" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-white/[0.12]" />
                  )}
                </div>
                <span className="text-[7px] lg:text-[9px] text-text-hint/60 mt-0.5 max-w-[48px] truncate text-center">
                  {step.label.length > 8 ? step.label.slice(0, 8) + '..' : step.label}
                </span>
              </div>
            </div>
          )
        })}

        <div className={`w-4 lg:w-full lg:min-w-6 h-[2px] ${progress >= 100 ? 'bg-success/40' : 'bg-white/[0.06]'}`} />
        <div className="shrink-0 flex flex-col items-center ml-1 lg:ml-2">
          <span className="text-[10px] lg:text-xs">{progress >= 100 ? '🏆' : '🎯'}</span>
          <span className="text-[8px] lg:text-[9px] text-text-hint mt-0.5">{t('goals.finish')}</span>
        </div>
      </div>
    </div>
  )
}

// ── Numeric Roadmap ──

export const GoalRoadmap: React.FC<GoalRoadmapProps> = React.memo(({ goal }) => {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const currentMonth = new Date().getMonth() + 1
  const currentYear = new Date().getFullYear()
  const isCurrentYear = goal.year === currentYear
  const isPastYear = goal.year < currentYear
  const progress = calculateProgress(goal)
  const [tooltipMonth, setTooltipMonth] = useState<number | null>(null)
  const isNumeric = goal.goal_type !== 'steps'

  // ALL hooks must be called before any conditional return
  const handleNodeTap = useCallback((month: number, e: React.MouseEvent): void => {
    e.stopPropagation()
    setTooltipMonth((prev) => (prev === month ? null : month))
  }, [])

  const { data: monthlyProgress } = useGoalMonthlyProgress(
    isNumeric ? goal.id : null,
    goal.year,
  )

  // Steps mode → different roadmap (AFTER all hooks)
  if (!isNumeric) {
    return <StepsRoadmap goal={goal} />
  }

  const milestones = useMemo((): Milestone[] => {
    const parsed = parseMilestones(goal.milestones)
    // Per-month targets (NOT cumulative)
    const base: Milestone[] = parsed.length > 0
      ? parsed
      : MONTH_KEYS.map(() => ({
          month: 0, // will be set below
          target: Math.round(goal.target_value / 12),
          achieved: 0,
        })).map((ms, i) => ({ ...ms, month: i + 1 }))

    // Merge real achieved values per month (NOT cumulative)
    if (monthlyProgress && monthlyProgress.length > 0) {
      const progressMap = new Map(monthlyProgress.map((p) => [p.month, p.achieved]))
      return base.map((ms) => ({
        ...ms,
        achieved: Math.round(progressMap.get(ms.month) ?? 0),
      }))
    }

    return base
  }, [goal.milestones, goal.target_value, monthlyProgress])

  // Auto-scroll to current month (mobile only)
  useEffect(() => {
    if (!scrollRef.current || !isCurrentYear) return
    if (scrollRef.current.scrollWidth <= scrollRef.current.clientWidth) return
    const nodeWidth = 56
    const scrollTo = (currentMonth - 1) * nodeWidth - scrollRef.current.clientWidth / 2 + nodeWidth / 2
    scrollRef.current.scrollLeft = Math.max(0, scrollTo)
  }, [currentMonth, isCurrentYear])

  const getNodeState = (month: number): 'achieved' | 'current' | 'future' => {
    if (isPastYear) return 'achieved'
    if (!isCurrentYear) return 'future'
    if (month < currentMonth) return 'achieved'
    if (month === currentMonth) return 'current'
    return 'future'
  }

  const nodeColors: Record<string, string> = {
    achieved: 'bg-success border-success/30',
    current: 'bg-primary border-primary/30',
    future: 'bg-white/[0.08] border-white/[0.08]',
  }

  const dotColors: Record<string, string> = {
    achieved: 'bg-success',
    current: 'bg-primary',
    future: 'bg-white/[0.12]',
  }

  return (
    <div className="mt-2 mb-1">
      <div
        ref={scrollRef}
        className="flex items-center gap-0 overflow-x-auto lg:overflow-x-visible scrollbar-hide pb-1 -mx-1 px-1"
      >
        {/* Start marker */}
        <div className="shrink-0 flex flex-col items-center mr-1 lg:mr-2">
          <span className="text-[10px] lg:text-xs">🏁</span>
          <span className="text-[8px] lg:text-[9px] text-text-hint mt-0.5">{t('goals.start')}</span>
        </div>

        {/* Month nodes */}
        {milestones.map((ms) => {
          const state = getNodeState(ms.month)
          const isToday = isCurrentYear && ms.month === currentMonth

          return (
            <div key={ms.month} className="shrink-0 lg:shrink lg:flex-1 flex items-center">
              <div className={`w-4 lg:w-full lg:min-w-6 h-[2px] ${
                state === 'achieved' ? 'bg-success/40' :
                state === 'current' ? 'bg-gradient-to-r from-success/40 to-primary/40' :
                'bg-white/[0.06]'
              }`} />

              <div className="flex flex-col items-center relative">
                <button
                  onClick={(e) => handleNodeTap(ms.month, e)}
                  className={`relative w-5 h-5 lg:w-6 lg:h-6 rounded-full border flex items-center justify-center tap-highlight-transparent ${nodeColors[state]}`}
                >
                  <div className={`w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full ${dotColors[state]}`} />
                  {isToday && (
                    <div className="absolute -bottom-1 lg:-bottom-1.5 w-0.5 h-1.5 lg:h-2 bg-primary rounded-full" />
                  )}
                </button>

                {tooltipMonth === ms.month && (
                  <div className="absolute -top-9 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap px-2 py-1 rounded-lg bg-surface border border-border shadow-lg text-[9px] text-text tabular-nums">
                    {t(`goals.months.${ms.month}`)}: {ms.achieved}/{ms.target} {goal.unit}
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-surface border-r border-b border-border rotate-45" />
                  </div>
                )}

                <span className={`text-[9px] lg:text-[11px] mt-0.5 lg:mt-1 font-medium tabular-nums ${
                  isToday ? 'text-primary' : 'text-text-hint'
                }`}>
                  {t(`goals.months.${ms.month}`)}
                </span>
                {ms.target > 0 && (
                  <span className="text-[7px] lg:text-[9px] text-text-hint/60 tabular-nums">
                    {ms.achieved}/{ms.target}
                  </span>
                )}
              </div>
            </div>
          )
        })}

        <div className={`w-4 lg:w-full lg:min-w-6 h-[2px] ${isPastYear || progress >= 100 ? 'bg-success/40' : 'bg-white/[0.06]'}`} />

        <div className="shrink-0 flex flex-col items-center ml-1 lg:ml-2">
          <span className="text-[10px] lg:text-xs">{progress >= 100 ? '🏆' : '🎯'}</span>
          <span className="text-[8px] lg:text-[9px] text-text-hint mt-0.5">{t('goals.finish')}</span>
        </div>
      </div>
    </div>
  )
})

GoalRoadmap.displayName = 'GoalRoadmap'
